import { animated, useSpring } from '@react-spring/web';
import { BlueLinkSecondaryUnderlined, H5 } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { Check } from '@liveauctioneers/hammer-ui-core/icons';
import { CheckoutProcessStep } from './types/CheckoutProcessStep.enum';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { ResizeObserver } from '@juggle/resize-observer';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import React from 'react';
import RowDivider from '../RowDivider';
import styled, { css, useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import useMeasure from 'react-use-measure';

type Props = {
    activeStep: CheckoutProcessStep;
    children: React.ReactNode;
    loading: boolean;
    onEdit?: (event: React.SyntheticEvent) => void;
    onSave: () => void;
    readOnly: boolean;
    saveButtonText: React.ReactNode;
    step: CheckoutProcessStep;
    stepRef?: React.MutableRefObject<HTMLDivElement>;
    title: React.ReactNode;
    valid: boolean;
};

const CheckoutStep = ({
    activeStep,
    children,
    loading,
    onEdit,
    onSave,
    readOnly,
    saveButtonText,
    step,
    stepRef,
    title,
    valid,
}: Props) => {
    const {
        breakpoints: { tabletWidthNum },
    } = useTheme();
    const mobileSize = useIsNarrowerThan(tabletWidthNum + 1);
    const active = step === activeStep;
    const beforeActive = step < activeStep;

    const showEditAction = beforeActive && valid && !readOnly;
    const showContent = beforeActive || active;

    const [contentContainerRef, { height }] = useMeasure({
        polyfill: ResizeObserver,
    });
    const slideInStyles = useSpring({
        to: {
            height: height,
            opacity: showContent ? 1 : 0,
        },
    });

    return (
        <StyledCheckoutStep
            $active={active}
            ref={stepRef}
        >
            <StyledTitle $darkGrey={!active && activeStep !== CheckoutProcessStep.Review}>{title}</StyledTitle>
            {showEditAction && (
                <>
                    <StyledCheckIconWrapper>
                        <Check
                            aria-hidden="true"
                            className="inline-flex"
                            data-testid={`${title}-valid`}
                            size="sm"
                        />
                    </StyledCheckIconWrapper>
                    {onEdit && (
                        <StyledBlueLinkSecondaryUnderlined onClick={(e) => !loading && onEdit(e)}>
                            <FormattedMessage id="checkout.stepEdit" />
                        </StyledBlueLinkSecondaryUnderlined>
                    )}
                </>
            )}
            <StyledContent style={slideInStyles}>
                {showContent && <div ref={contentContainerRef}>{children}</div>}
            </StyledContent>
            <StyledSaveButtonWrapper $active={active}>
                {active && (
                    <StyledSaveButton
                        disabled={!valid}
                        loading={loading}
                        onClick={onSave}
                        primary
                        small={!mobileSize}
                        testid="next-checkout-step"
                    >
                        {saveButtonText}
                    </StyledSaveButton>
                )}
            </StyledSaveButtonWrapper>
            {beforeActive && <StyledRowDividerBottom />}
            {active && <StyledRowDividerBottomLarge />}
        </StyledCheckoutStep>
    );
};

export default CheckoutStep;

const StyledCheckoutStep = styled.div<{
    $active: boolean;
}>`
    display: grid;
    grid-template:
        'divider-top'
        'title'
        'content'
        'save'
        'divider-bottom';
    ${({ $active }) =>
        !$active &&
        css`
            grid-template:
                'divider-top divider-top'
                'title check'
                'content actions'
                'save save'
                'divider-bottom divider-bottom'
                / auto 64px;
        `};
`;

const StyledTitle = styled(H5)`
    grid-area: title;
    margin-bottom: 24px;
`;

const StyledBlueLinkSecondaryUnderlined = styled(BlueLinkSecondaryUnderlined)`
    grid-area: actions;
    display: flex;
    justify-content: flex-end;
    height: fit-content;
`;

const StyledCheckIconWrapper = styled.div`
    grid-area: check;
    display: flex;
    justify-content: flex-end;
    margin-bottom: 24px;
`;

const StyledContent = styled(animated.div)`
    grid-area: content;
    overflow: hidden;
    margin-bottom: 32px;
`;

//this wrapper exists purely to make the spacing between steps work as design wants it.
const StyledSaveButtonWrapper = styled.div<{ $active: boolean }>`
    margin-bottom: 32px;
    grid-area: save;
    ${({ $active }) => {
        if (!$active) {
            return css`
                @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
                    margin-bottom: 12px;
                }
            `;
        }
    }};
`;

const StyledSaveButton = styled(Button)`
    max-width: fit-content;
    padding: 16px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        max-width: 100%;
        width: 100%;
    }
`;

const StyledRowDividerBottom = styled(RowDivider)`
    grid-area: divider-bottom;
`;

const StyledRowDividerBottomLarge = styled(RowDivider)`
    grid-area: divider-bottom;
    margin-bottom: 64px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 24px;
    }
`;
